import React from 'react';
import { CustomerServiceOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Spin, Button, Space, Typography, Skeleton, Tooltip } from 'antd';
import PreparingIcon from '@icons/preparing.svg';
import { CardWrapper, Layer, Loader } from './styled';
import ProgressBar from './ProgressBar';
import { calcProgressDuration, secondsToMinutes } from './utils';
import { HEAVY_METRICS_MESSAGE } from '@utils/constants';
const { Text } = Typography;

const RETRY_TXT = {
  msg: 'Please try again',
  btn: 'Retry',
  err: 'Error loading data',
  heavyMetricMsg: HEAVY_METRICS_MESSAGE,
};

const DONE_TXT = {
  msg: 'Please try again later or reach out to Conviva Support.',
  btn: 'Contact',
  err: 'Something went wrong',
};

const getLoadingText = (maxQueryTime, messageType) => {
  const formattedTime = secondsToMinutes(maxQueryTime);
  switch (messageType) {
    case 'heavyMetric':
      return `We are aggregating millions of events to fetch the data. Unique Devices based metric queries for duration greater than 30 days can take a long time to load.`;
    default:
      return `We are aggregating millions of events and expect data to display within ${formattedTime}.`;
  }
};

function WarningIcon() {
  return <WarningOutlined style={{ color: '#ff6347' }} className="mr-2" />;
}

export function DefaultLayer({
  progress,
  duration,
  showMsg = false,
  showHeavyMetricMessage = false,
  type,
  queued = false,
}) {
  const maxQueryTime = calcProgressDuration(duration);

  if (showHeavyMetricMessage) {
    type = 'heavyMetric';
  }

  return (
    <>
      <Layer data-testid="layer" key="layer" />
      <Loader data-testid="loader" key="loader">
        {queued ? (
          <Skeleton.Node
            style={{ height: 50, border: '1px solid #e1e2e4' }}
            key="queue-wrapper"
            active={true}
          >
            Queued
          </Skeleton.Node>
        ) : (
          <Space key="progress-wrapper" direction="vertical">
            {progress ? <ProgressBar key="progress-bar" duration={duration} /> : <Spin />}
            {showMsg && <Text key="loading-message">{getLoadingText(maxQueryTime, type)}</Text>}
          </Space>
        )}
      </Loader>
    </>
  );
}

export function RetryCard({
  onClick,
  horizontal = false,
  showHeavyMetricMessage = false,
  compact = false,
}) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        {RETRY_TXT.err}
      </span>
      <div className="message">
        {showHeavyMetricMessage ? RETRY_TXT.heavyMetricMsg : RETRY_TXT.msg}
      </div>
      <Button onClick={onClick} type="primary" ghost icon={<ReloadOutlined />}>
        {RETRY_TXT.btn}
      </Button>
    </CardWrapper>
  );
}

export function ContactCard({ onClick, horizontal = false }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        {DONE_TXT.err}
      </span>
      <div className="message">{DONE_TXT.msg}</div>
      <Button
        id="contact-button"
        onClick={onClick}
        type="primary"
        ghost
        icon={<CustomerServiceOutlined />}
      >
        <a href="/app/pulse/support" target="_BLANK">
          {DONE_TXT.btn}
        </a>
      </Button>
    </CardWrapper>
  );
}

export function PreparingData({ horizontal = false }) {
  return (
    <>
      <Layer key="layer" />
      <Loader style={{ width: 'auto', maxWidth: 'none' }} key="loader">
        <CardWrapper horizontal={horizontal}>
          <div style={{ fontSize: '3em' }}>
            <PreparingIcon />
          </div>
          <div style={{ fontWeight: 400 }}>Preparing Data</div>
          <div style={{ fontSize: '0.9em', fontWeight: 300 }}>
            We are aggregating millions of events and expect data to display within 20 seconds.
          </div>
        </CardWrapper>
      </Loader>
    </>
  );
}

export function QueryError({ horizontal = false }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        Query Error
      </span>
      <div className="message">
        Missing or invalid dimension while processing the query. Please update the filter and try
        again.
      </div>
    </CardWrapper>
  );
}

export const QueryDimensionErrorComp = () => (
  <Tooltip
    placement="top"
    title={'The selected dimension does not exist. Please select another dimension.'}
  >
    <WarningOutlined style={{ color: '#ff2f36', marginRight: 10 }} /> Query Error
  </Tooltip>
);

export function ServerError({ horizontal = false, message = 'Unknown server error' }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        Query Error
      </span>
      <div className="message" style={{ width: 'fit-content' }}>
        {message}
      </div>
    </CardWrapper>
  );
}
